import { RefObject } from 'preact';
import { useCallback } from 'preact/hooks';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useUser } from '#hooks/UNSAFE_useUser';
import { findElementByKey, keyExtractor, getPrevNextKey } from '#utils/PRIVATE_collectionUtils';
import { isKeyDefined } from '#utils/UNSAFE_keys';
import { useNavigation } from '#hooks/PRIVATE_useListCommon';
import { TREE_ITEM_SELECTOR } from './TreeViewUtils';

export function useTreeNavigation<K extends string | number>(
  data: any | null,
  rootRef: RefObject<HTMLDivElement>,
  isExpanded: (key: K) => boolean,
  idExtractor: (elem: Element) => string
) {
  const { direction } = useUser();

  const arrowKeyAndHandlers = {
    ArrowUp: (key?: K) => getPrevNextKey(rootRef.current, key, true, TREE_ITEM_SELECTOR),
    ArrowDown: (key?: K) => getPrevNextKey(rootRef.current, key, false, TREE_ITEM_SELECTOR)
  };

  const { navigationProps, currentKey, onCurrentKeyChange, showFocusRing } = useNavigation(
    data,
    rootRef,
    idExtractor,
    TREE_ITEM_SELECTOR,
    arrowKeyAndHandlers,
    undefined, // currentItemOverride,
    undefined, //onPersistCurrentItem,
    undefined,
    undefined,
    true, // should always allow shift key
    false, // TODO: update allowTabbableMode when F2 mode is added
    true,
    false
  );

  const itemKeyExtractor = useCallback(
    (element: HTMLElement) => {
      if (element.getAttribute('role') === 'tree') {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, TREE_ITEM_SELECTOR) as K;
    },
    [currentKey, keyExtractor]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const isRtl = direction === 'rtl';
        const isLeftArrow = event.key === 'ArrowLeft' && !isRtl;
        const itemKey = itemKeyExtractor(event.target as HTMLElement);
        if (itemKey) {
          const contains = isExpanded(itemKey);
          // arrow left on a collapsed node, go to its parent
          if (isLeftArrow && !contains && rootRef.current) {
            const item = findElementByKey(rootRef.current, itemKey, TREE_ITEM_SELECTOR);
            if (item) {
              const treeDepth = item.getAttribute('aria-level');
              const parent =
                treeDepth !== null &&
                findParentElementByKey(
                  rootRef.current,
                  itemKey,
                  Number(treeDepth),
                  TREE_ITEM_SELECTOR
                );
              const newKey = parent && itemKeyExtractor(parent as HTMLElement);
              if (isKeyDefined(newKey)) {
                onCurrentKeyChange?.({ value: newKey as K });
              }
            }
          } else if (!isLeftArrow && contains) {
            // arrow right on an expanded node, go to its first child
            const newKey = getPrevNextKey(rootRef.current, itemKey, false, TREE_ITEM_SELECTOR)();
            if (isKeyDefined(newKey)) {
              onCurrentKeyChange?.({ value: newKey as K });
            }
          }
        }
      }
    },
    [direction, isExpanded, onCurrentKeyChange, itemKeyExtractor]
  );

  const treeNavigationProps = mergeProps(navigationProps, { onKeyUp });
  return { treeNavigationProps, currentKey, showFocusRing };
}

const findParentElementByKey = <K extends string | number>(
  root: HTMLElement | null,
  key: K | null,
  treeDepth: number,
  itemSelector: string
) => {
  if (root && key !== null) {
    const items = Array.from(root.querySelectorAll(itemSelector));
    const index = items.findIndex((i) => (i as HTMLElement).dataset['ojKey'] == key);
    for (let i = index; i >= 0; i--) {
      const level = items[i].getAttribute('aria-level');
      if (level !== null && Number(level) == treeDepth - 1) {
        return items[i];
      }
    }
  }
  return null;
};
